/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, Plus, Activity } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { SectionPageLayout } from '@/components/layout'
import { getOverviewStats, getTrendData, getModelDistribution } from './api'
import { KpiCards } from './components/kpi-cards'
import { TrafficChart } from './components/traffic-chart'
import { DistributionChart } from './components/distribution-chart'
import type { TrendTimeRange } from './types'

export function DataOverview() {
  const { t } = useTranslation()
  const [trendRange, setTrendRange] = useState<TrendTimeRange>('24h')

  const statsQuery = useQuery({
    queryKey: ['data-overview', 'stats'],
    queryFn: getOverviewStats,
    staleTime: 60 * 1000,
  })

  const trendQuery = useQuery({
    queryKey: ['data-overview', 'trend', trendRange],
    queryFn: () => getTrendData(trendRange),
    staleTime: 60 * 1000,
  })

  const distributionQuery = useQuery({
    queryKey: ['data-overview', 'distribution'],
    queryFn: getModelDistribution,
    staleTime: 60 * 1000,
  })

  const stats = statsQuery.data ?? null

  return (
    <SectionPageLayout>
      <div className='flex flex-col gap-1'>
        <SectionPageLayout.Title>{t('Data Overview')}</SectionPageLayout.Title>
        <SectionPageLayout.Description>
          {t('Real-time monitoring of platform operation status and core metrics')}
        </SectionPageLayout.Description>
      </div>

      <SectionPageLayout.Content>
        <div className='flex flex-col gap-4'>
          {/* Header actions */}
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <div />
            <div className='flex items-center gap-2'>
              <Button variant='outline' size='sm'>
                <Download data-icon='inline-start' />
                {t('Export Report')}
              </Button>
              <Button size='sm'>
                <Plus data-icon='inline-start' />
                {t('Create Project')}
              </Button>
            </div>
          </div>

          {/* KPI Cards */}
          <KpiCards
            stats={stats}
            loading={statsQuery.isLoading}
          />

          {/* Charts row */}
          <div className='grid grid-cols-1 gap-4 lg:grid-cols-3'>
            {/* Traffic chart - takes 2/3 */}
            <div className='lg:col-span-2'>
              <div className='flex flex-col gap-3'>
                <Tabs
                  value={trendRange}
                  onValueChange={(value) =>
                    setTrendRange(value as TrendTimeRange)
                  }
                >
                  <TabsList className='h-auto w-fit'>
                    <TabsTrigger value='24h'>{t('24h')}</TabsTrigger>
                    <TabsTrigger value='7d'>{t('7d')}</TabsTrigger>
                    <TabsTrigger value='30d'>{t('30d')}</TabsTrigger>
                  </TabsList>
                </Tabs>
                <TrafficChart
                  data={trendQuery.data ?? []}
                  loading={trendQuery.isLoading}
                  range={trendRange}
                />
              </div>
            </div>

            {/* Distribution chart - takes 1/3 */}
            <div className='lg:col-span-1'>
              <DistributionChart
                data={distributionQuery.data ?? []}
                loading={distributionQuery.isLoading}
              />
            </div>
          </div>
        </div>
      </SectionPageLayout.Content>
    </SectionPageLayout>
  )
}
